import * as React from "react";
import Image from "antd/es/image";
import Button from "antd/es/button";
import Spin from "antd/es/spin";
import { createTransfer, validateTransfer } from "@solana/pay";
import { BigNumber } from "bignumber.js";
import { useWallet } from "@solana/wallet-adapter-react";
import { Connection, PublicKey } from "@solana/web3.js";
import message from "antd/es/message";
import { useMutation } from "@tanstack/react-query";
import { FormModel } from "./types";
import { SolanaConnect } from "./SolanaConnect";
import settings from "../settings.json";
import { ErrorDisplay } from "./ErrorDisplay";
import { useVerifyReserves } from "./useVerifyReserves";

export interface SolanaPayProps {
    formModel: FormModel;
    setTransactionId: (txId: string) => void,
}

const connection = new Connection(settings.helius, "finalized");

export const SolanaPay: React.FunctionComponent<SolanaPayProps> = (props) => {
    const [sender, setSender] = React.useState<string>();
    const { sendTransaction, connected, connect } = useWallet();
    const verify = useVerifyReserves();
    const [messageApi, contextHolder] = message.useMessage();
    const pay = useMutation({
        mutationKey: ["onpay"],
        mutationFn: async () => {
            try {
                if (!connected) {
                    await connect();
                }
                const recipient = new PublicKey(settings.seller.sol);
                const amount = BigNumber(props.formModel.fromAmount);
                const splToken = new PublicKey(props.formModel.token === "LLD" ? settings.lld.sol : settings.usdt.sol);
                const message = "LiberStake transfer from Solana";
                const transaction = await createTransfer(
                    connection,
                    new PublicKey(sender!),
                    {
                        recipient,
                        amount,
                        reference: new PublicKey(props.formModel.orderId),
                        splToken,
                        memo: message,
                    },
                );
                const verified = await verify.mutateAsync(props.formModel);
                if (!verified) {
                    messageApi.error("Not enough reserves, try again later");
                    return;
                }
                const signature = await sendTransaction(
                    transaction,
                    connection,
                );
                const latestBlockhash = await connection.getLatestBlockhash();
                await connection.confirmTransaction(
                    {
                        signature,
                        blockhash: latestBlockhash.blockhash,
                        lastValidBlockHeight: latestBlockhash.lastValidBlockHeight,
                    },
                    "finalized",
                );
    
                await validateTransfer(connection, signature, {
                    recipient: new PublicKey(settings.seller.sol),
                    amount: new BigNumber(amount) as any,
                    splToken,
                    reference: new PublicKey(props.formModel.orderId),
                })
                props.setTransactionId(signature);
            } catch (e) {
                console.error(e);
                throw e;
            }
        }
    });
    return (
        <>
            {contextHolder}
            {sender && !pay.isError && (
                <Button
                    icon={pay.isPending
                        ? <Spin />
                        : <Image src={require("../solana.svg").default} width="22px" height="22px" preview={false} />}
                    className="SolanaButton SolanaButton--payment SolanaButton--main"
                    type="primary"
                    disabled={pay.isPending || pay.isSuccess}
                    onClick={() => pay.mutate()}
                >
                    {pay.isPending ? "Loading..." : "Pay"}
                </Button>
            )}
            {pay.isError && (
                <ErrorDisplay
                    orderId={props.formModel.orderId}
                />
            )}
            <SolanaConnect
                selectWallet={(address) => setSender(address)}
                payment
                label="Connect wallet"
                token={props.formModel.token}
            />
        </>
    );
};